import { Link } from "react-router-dom";
import type { Athlete } from "@/data/athletes";
import FavoriteButton from "@/components/FavoriteButton";
import AthleteSocialLinks, { type AthleteLinks } from "@/components/AthleteSocialLinks";
import { toPlainText } from "@/lib/text";

export type AthleteCardTeam = { id: string; slug: string; name: string };

const AthleteCard = ({
  athlete,
  favoriteId,
  hasProfile = true,
  links,
  teams,
}: {
  athlete: Athlete;
  // Legacy callback (no longer used) — kept optional for compatibility.
  onOpen?: (id: string) => void;
  favoriteId?: string;
  hasProfile?: boolean;
  links?: AthleteLinks;
  teams?: AthleteCardTeam[];
}) => {
  return (
    <div className="group text-left relative rounded-2xl overflow-hidden bg-card shadow-card hover:shadow-bold transition-all duration-500 hover:-translate-y-1 block">
      {hasProfile && (
        <Link
          to={`/athlete/${athlete.id}`}
          aria-label={athlete.name}
          className="absolute inset-0 z-[1] rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        />
      )}
      <div className="aspect-[3/4] overflow-hidden">
        <img
          src={athlete.image}
          alt={athlete.name}
          loading="lazy"
          width={768}
          height={1024}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-background/90 backdrop-blur text-[10px] font-semibold uppercase tracking-wider">
        {athlete.sport}
      </div>
      {favoriteId && (
        <div className="absolute top-2 right-2 z-[2]">
          <FavoriteButton athleteId={favoriteId} size="sm" />
        </div>
      )}
      <div className="p-3">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="font-display text-base leading-tight truncate">{athlete.name}</h3>
          <span className="text-[10px] text-muted-foreground shrink-0">{athlete.country}</span>
        </div>
        {teams && teams.length > 0 && (
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mt-1 truncate">
            {teams.map((t) => t.name).join(" · ")}
          </p>
        )}
        {toPlainText(athlete.tagline) && (
          <p className="serif text-sm text-primary mt-0.5 truncate">{toPlainText(athlete.tagline)}</p>
        )}
        {links && (
          <AthleteSocialLinks
            links={links}
            variant="compact"
            className="mt-2 relative z-[2]"
            onClickStop
          />
        )}
      </div>
    </div>
  );

};

export default AthleteCard;
